import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    ActionIcon,
    AppShell,
    Center,
    Group,
    Space,
    Tabs,
    TextInput,
    Title,
} from '@mantine/core';
import {
    Helm,
    Placemark,
    PlacemarkFill,
    Plus,
    Star,
    StarFill,
} from 'framework7-icons-plus/react';
import { useColorScheme } from "@mantine/hooks";
import { IconSearch } from '@tabler/icons-react';
import { AppShellType, Guide, Place, PlaceClassType, Trip } from '../../lib/interfaces';
import { DeferredLoad } from "../DeferredLoad";
import { Overview } from "../Overview/Overview";
import { SavedPlacesSegmentedControl } from '../SavedPlacesList/SavedPlacesList';
import { TripsList } from '../TripsList/TripsList';
import { UserContext } from '../../lib/context';
import {
    GUIDES_TAB_NAME,
    MAP_TAB_NAME,
    OVERVIEW_TAB_NAME,
    TODO_TAB_NAME,
    SAVED_PLACES_TAB_NAME,
    TRIPS_TAB_NAME,
} from '../TravelScrollMenu';
import { showAddEditTripSavedPlace } from '../AddEditPlace/addeditplace';
import { SearchableMap } from "../GoogleMapPlaces/SearchableMap";
import { SheetsContext } from "../Sheets";
import { blurOnEnterKey } from "../../lib/eventHandlers";
import { useQueryParams } from '../../lib/urls';
import { SpeedDialAffix } from "../SpeedDialAffix";
import { TSLoader } from "../TSLoader";
import { ClearTextAreaRightSection } from "../AddEditTrip/ClearTextAreaRightSection";
import { extraFooterClasses } from "../../lib/appShellUtils";
import { tabsProps } from "../tabs";
import { TSAppShell } from '../TSAppShell/TSAppShell';

import shellClasses from "../TSAppShell/ShellStyles.module.css";
import { GuidesList } from '../GuidesList/GuidesList';
import { showAddEditGuide } from '../AddEditGuide/AddEditGuide';
import { hasSeenDisplayNamePrompt, showDisplayNamePrompt } from '../DisplayNamePrompt/DisplayNamePrompt';
import { showPasskeyPrompt } from '../PasskeyPrompt/PasskeyPrompt';
import { hasDismissedPasskeyPrompt } from '../../lib/passkeyPrompt';
import { isPasskeySupported, listPasskeys } from '../../lib/passkeys';
import { useFirebaseAuth } from '../../lib/firebase';
import { ProblemsList } from '../ProblemsList/ProblemsList';

function ListsHeaderSearchBar({ onSearchQueryChanged }: { onSearchQueryChanged: (newQuery: string) => unknown }) {
    const [ searchVisible, setSearchVisible ] = useState(false);

    if (searchVisible) {
        return <>
            <TextInput
                placeholder='Search...'
                autoFocus
                onChange={event => onSearchQueryChanged(event.currentTarget.value)}
                enterKeyHint='done'
                onKeyUp={blurOnEnterKey}
                rightSection={<ClearTextAreaRightSection onClear={() => {
                    onSearchQueryChanged('');
                    setSearchVisible(false);
                }} />}
            />
        </>;
    } else {
        return <ActionIcon onClick={() => setSearchVisible(true)}>
            <IconSearch height={24} width={24} color='white' />
        </ActionIcon>;
    }
}

export function HomeAppShell({ userid, tab, selected, userTrips, userGuides, savedUidPlaces }: {
    userid: string | undefined,
    tab: string,
    selected: string | null,
    userTrips: Trip[] | undefined,
    userGuides: Guide[] | undefined,
    savedUidPlaces: Place[] | null,
}) {
    const colorScheme = useColorScheme();
    const searchParams = useQueryParams();
    const user = useContext(UserContext);
    const sheets = useContext(SheetsContext);
    const [ showSpeedDial, setShowSpeedDial ] = useState(false);

    const [ savedListsSearchQuery, setSavedListsSearchQuery ] = useState<string | undefined>();
    const [ isDrawerOpen, setDrawerOpen ] = useState(false);

    // First-login onboarding: email-link sign-ins carry no display name, so once the user's record
    // has loaded and is still missing one, prompt for it (skippable). It can always be set later in
    // Settings. We wait for both the public and private records so we don't prompt before we know
    // whether the user already has a name or has already answered; the prompt marks itself
    // answered, so it returns if the user never actually responded to it.
    const promptedForDisplayNameRef = useRef(false);
    useEffect(() => {
        if (promptedForDisplayNameRef.current
            || user.status !== 'logged-in'
            || !user.publicDataLoaded
            || !user.privateDataLoaded
            || !user.firebaseUserId
            || !!user.displayName?.trim()
            || user.displayNamePromptDismissed
            || hasSeenDisplayNamePrompt(user.firebaseUserId)) {
            return;
        }
        promptedForDisplayNameRef.current = true;
        showDisplayNamePrompt(sheets, { uid: user.firebaseUserId, isOnboarding: true });
    }, [
        user.status,
        user.publicDataLoaded,
        user.privateDataLoaded,
        user.firebaseUserId,
        user.displayName,
        user.displayNamePromptDismissed,
        sheets,
    ]);

    // Offer a passkey once, after sign-in. Gated on the device actually having a platform
    // authenticator, and on the account not already having one -- unlike the display-name prompt
    // this decision is stored per-device, so someone who enrolled on their phone is still offered on
    // their laptop. Sequenced after the display-name prompt so a new user isn't handed two sheets at
    // once.
    const passkeyAuth = useFirebaseAuth();
    const promptedForPasskeyRef = useRef(false);
    useEffect(() => {
        if (promptedForPasskeyRef.current
            || user.status !== 'logged-in'
            || !user.privateDataLoaded
            || !user.firebaseUserId
            || hasDismissedPasskeyPrompt(user.firebaseUserId)
            // Don't stack a second sheet on top of the first-login name prompt.
            || (!user.displayName?.trim() && !user.displayNamePromptDismissed)) {
            return;
        }
        promptedForPasskeyRef.current = true;
        const uid = user.firebaseUserId;
        (async () => {
            if (!await isPasskeySupported()) {
                return;
            }
            try {
                // An empty list means this device certainly has no passkey, which is the case worth
                // prompting for. A non-empty list may still be all other devices, but we would
                // rather under-prompt than nag someone who has already adopted the feature.
                if ((await listPasskeys(passkeyAuth)).length > 0) {
                    return;
                }
            } catch {
                return; // if we can't tell, don't nag
            }
            showPasskeyPrompt(sheets, { uid });
        })();
    }, [ user.status, user.privateDataLoaded, user.firebaseUserId, user.displayName,
        user.displayNamePromptDismissed, sheets, passkeyAuth ]);

    const savedPlacesLoaded = savedUidPlaces !== null;

    return (
        <>
            <SpeedDialAffix isVisible={showSpeedDial} closeSpeedDial={() => setShowSpeedDial(false)} />

            <Tabs
                {...tabsProps(colorScheme)}
                value={tab}
                style={{ '--tscroll-footer-height': 'calc(env(safe-area-inset-bottom) + 80px)' } as React.CSSProperties}
                onChange={value => {
                    searchParams.replace(
                        {
                            tab: value ?? GUIDES_TAB_NAME,
                            place: selected ?? undefined,
                            seg: value === 'Saved' ? 'MySaveds' : undefined,
                        },
                        [ 'seg' ]);
                }}
            >
                <TSAppShell
                    ariaLabel='Travel Scroll'
                    // The map should fill the full width on wide screens (only the bottom
                    // sheet is inset); centring is for the other tabs.
                    centerOnWide={tab !== MAP_TAB_NAME}
                    drawerState={{ isDrawerOpen, setDrawerOpen }}
                    header={tab === MAP_TAB_NAME ? undefined : {
                        title: tab === TODO_TAB_NAME ? 'To Do' : tab,
                        rightSide: tab === SAVED_PLACES_TAB_NAME
                            ? <div style={{
                                display: 'flex',
                                flex: '1',
                                marginLeft: 'auto',
                                alignItems: 'center',
                                flexDirection: 'row-reverse',
                                paddingRight: '0.5rem',
                            }}>
                                <ListsHeaderSearchBar onSearchQueryChanged={setSavedListsSearchQuery} />
                            </div>
                        : undefined,
                    }}
                >
                    <Tabs.Panel value={SAVED_PLACES_TAB_NAME}>
                        { !savedPlacesLoaded &&
                            <TSLoader id='HomeAppShell-SavedPlaces' layout='fill-content-area' />}
                        { savedPlacesLoaded &&
                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                {!user.isLoggedIn && <Space h={73} />}
                                <SavedPlacesSegmentedControl
                                    searchQuery={savedListsSearchQuery}
                                    places={savedUidPlaces!}
                                    uid={userid}
                                />
                            </div> }
                    </Tabs.Panel>
                    <Tabs.Panel value={TRIPS_TAB_NAME}>
                        { !userTrips && <TSLoader id='HomeAppShell-Trips' layout='fill-content-area' /> }
                        { !!userTrips &&
                            <>
                                {user.status === 'logged-out' && <Space h={73} />}
                                <DeferredLoad
                                    trigger={tab === TRIPS_TAB_NAME}
                                    provider={() => <TripsList userTrips={userTrips} />}
                                    fallback={null}
                                />
                            </> }
                    </Tabs.Panel>
                    <Tabs.Panel value={GUIDES_TAB_NAME}>
                        { !userGuides && <TSLoader id='HomeAppShell-Guides' layout='fill-content-area' /> }
                        { !!userGuides &&
                            <>
                                {user.status === 'logged-out' && <Space h={73} />}
                                <DeferredLoad
                                    trigger={tab === GUIDES_TAB_NAME}
                                    provider={() => <GuidesList
                                        userGuides={userGuides}
                                        userId={userid}
                                    />}
                                    fallback={null}
                                />
                            </> }
                    </Tabs.Panel>
                    <Tabs.Panel value={OVERVIEW_TAB_NAME}>
                        <>
                            {user.status === 'logged-out' && <Space h={73} />}
                            <DeferredLoad
                                trigger={tab === OVERVIEW_TAB_NAME}
                                provider={() => <Overview />}
                                fallback={null}
                            />
                        </>
                    </Tabs.Panel>
                    <Tabs.Panel value={TODO_TAB_NAME}>
                        <>
                            {userTrips ? <ProblemsList trips={userTrips} /> : <Space h={73} />}
                        </>
                    </Tabs.Panel>
                    <Tabs.Panel style={{ paddingTop: 0 }} value={MAP_TAB_NAME} id='mapplacescontainer'>
                        <DeferredLoad
                            trigger={tab === MAP_TAB_NAME}
                            provider={() =>
                                <SearchableMap
                                    placesToSearch={[]}
                                    currentUserId={userid}
                                    isMapVisible={tab === 'Map'}
                                    appShellType={AppShellType.Home}
                                    selected={selected}
                                    setSelected={id => searchParams.route({ place: id }, [], 'replace')}
                                    emptyQueryBehavior='match-none'
                                    refreshRoute={`/?tab=${MAP_TAB_NAME}`}
                                    chipConfig={{
                                        tripGuideChips: true,
                                        recents: true,
                                        savedPlaces: true,
                                        searchEngine: true,
                                    }}
                                    toggleTravelScrollDrawer={() => setDrawerOpen(!isDrawerOpen)}
                                />}
                            fallback={null}
                        />
                    </Tabs.Panel>
                    <AppShell.Footer className={`${shellClasses.footer} ${extraFooterClasses(tab)}`}>
                        <Group justify='space-between' gap="xs">
                            <Group justify='space-between' grow gap="xs" style={{ flex: 7 }}>
                                <Tabs.List>
                                    <Tabs.Tab style={{ flex: 1 }} value={GUIDES_TAB_NAME} w={30} aria-label={GUIDES_TAB_NAME}>
                                        <Center>
                                            {tab === GUIDES_TAB_NAME ?
                                                (<Helm height={18} width={18} />) :
                                                (<Helm height={18} width={18} />)}
                                        </Center>
                                        <Space h='xs' />
                                        <Title style={{ textAlign: 'center' }} order={5}>
                                            {GUIDES_TAB_NAME}
                                        </Title>
                                    </Tabs.Tab>
                                    <Tabs.Tab style={{ flex: 1 }} value={SAVED_PLACES_TAB_NAME} w={30}>
                                        <Center>
                                            {tab === SAVED_PLACES_TAB_NAME ?
                                                (<StarFill height={18} width={18} />) :
                                                (<Star height={18} width={18} />)}
                                        </Center>
                                        <Space h='xs' />
                                        <Title style={{ textAlign: 'center' }} order={5}>
                                            {SAVED_PLACES_TAB_NAME}
                                        </Title>
                                    </Tabs.Tab>
                                    <Tabs.Tab style={{ flex: 1 }} value={MAP_TAB_NAME} w={30} aria-label='Map Nav'>
                                        <Center>
                                            {tab === MAP_TAB_NAME ?
                                                (<PlacemarkFill height={18} width={18} />) :
                                                (<Placemark height={18} width={18} />)}
                                        </Center>
                                        <Space h='xs' />
                                        <Title style={{ textAlign: 'center' }} order={5}>
                                            {MAP_TAB_NAME}
                                        </Title>
                                    </Tabs.Tab>
                                </Tabs.List>
                            </Group>
                            <Center style={{ flex: 1 }} pr='xs'>
                                <ActionIcon
                                    className='tscroll-floating-action-button'
                                    variant="filled"
                                    size="xl"
                                    radius='xl'
                                    onClick={() => {
                                        if (tab === SAVED_PLACES_TAB_NAME) {
                                            showAddEditTripSavedPlace(sheets, { placeclass: PlaceClassType.Saved }, undefined);
                                        } else if (tab === GUIDES_TAB_NAME || tab === MAP_TAB_NAME) {
                                            showAddEditGuide(sheets, {});
                                        } else {
                                            setShowSpeedDial(true);
                                        }
                                    }}>
                                    <Plus height={18} width={18} />
                                </ActionIcon>
                            </Center>
                        </Group>
                    </AppShell.Footer>
                </TSAppShell>
            </Tabs>
        </>
    );
}
